import React, { useState, useEffect, useRef } from 'react';
import { api, getToken } from '../api';
import { useAppContext } from '../context/AppContext';

const WS_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('http', 'ws')
  : `${window.location.protocol.replace('http', 'ws')}//${window.location.host}/svc`;

export default function ChatScreen() {
  const { user } = useAppContext();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);

  // Загрузка истории
  useEffect(() => {
    api.chat.messages(30, 0)
      .then(data => {
        console.log('[CHAT] messages:', data);
        setMessages(data.items || []);
      })
      .catch(e => console.log('[CHAT] error:', e))
      .finally(() => setLoading(false));
  }, []);

  // WebSocket
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/chat/ws/${token}`);
    wsRef.current = ws;

    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => {};
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setMessages(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          const tempIdx = prev.findIndex(m => String(m.id).startsWith('temp_') && m.text === msg.text);
          if (tempIdx !== -1) return prev.map((m, i) => i === tempIdx ? msg : m);
          return [...prev, msg];
        });
      } catch {}
    };

    return () => {
      ws.onclose = null;
      ws.close();
      wsRef.current = null;
      setWsConnected(false);
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const sendMessage = () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      alert('Нет соединения с сервером');
      return;
    }

    const tempId = `temp_${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempId, user_id: user?.id, user_name: user?.name,
      text: trimmed, is_from_admin: false, created_at: new Date().toISOString(),
    }]);
    setText('');
    ws.send(JSON.stringify({ text: trimmed }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleClear = async () => {
    if (!window.confirm('Удалить всю историю сообщений?')) return;
    await api.chat.clearMessages();
    setMessages([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--card)' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>💬 Поддержка</div>
          <div style={{ fontSize: 12, color: wsConnected ? '#4CAF50' : 'var(--text-light)', marginTop: 2 }}>
            {wsConnected ? '● Подключено' : 'Ответим в ближайшее время'}
          </div>
        </div>
        <button onClick={handleClear} style={{ background: 'none', color: '#FF3B30', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Очистить</button>
      </div>

      {/* Сообщения */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-light)' }}>Загрузка...</div>
        ) : messages.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
            <span style={{ fontSize: 48 }}>💬</span>
            <span style={{ color: 'var(--text-light)', fontSize: 15 }}>Напишите нам — мы поможем!</span>
          </div>
        ) : messages.map(msg => {
          const isAdmin = msg.is_from_admin;
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-start' : 'flex-end', marginBottom: 6 }}>
              <div style={{
                maxWidth: '78%', borderRadius: 16, padding: '8px 14px',
                background: isAdmin ? 'var(--card)' : 'var(--primary)',
                border: isAdmin ? '1px solid var(--border)' : 'none',
                borderBottomLeftRadius: isAdmin ? 4 : 16,
                borderBottomRightRadius: isAdmin ? 16 : 4,
              }}>
                {isAdmin && <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700, marginBottom: 2 }}>Поддержка</div>}
                <div style={{ fontSize: 14, lineHeight: 1.5, color: isAdmin ? 'var(--text)' : '#fff', whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                <div style={{ fontSize: 10, marginTop: 3, textAlign: 'right', color: isAdmin ? 'var(--text-light)' : 'rgba(255,255,255,0.7)' }}>
                  {new Date(msg.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Поле ввода */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, padding: '8px 16px', borderTop: '1px solid var(--border)', background: 'var(--card)' }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Введите сообщение..."
          maxLength={1000}
          rows={1}
          style={{ flex: 1, minHeight: 40, maxHeight: 120, background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 20, padding: '10px 16px', fontSize: 14, color: 'var(--text)', outline: 'none', resize: 'none', fontFamily: 'inherit' }}
        />
        <button
          onClick={sendMessage}
          onTouchEnd={e => { e.preventDefault(); sendMessage(); }}
          disabled={!text.trim()}
          style={{ width: 40, height: 40, borderRadius: 20, background: 'var(--primary)', color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: !text.trim() ? 0.4 : 1, flexShrink: 0 }}
        >
          ➤
        </button>
      </div>
    </div>
  );
}
